import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { apiRequest } from "../../shared/api/client";
import type { Week39PackagingSnapshot } from "./week39-types";

const noteSchema = z.object({ date: z.iso.date(), workOrderNo: z.string(), productCode: z.string(), text: z.string(),
  version: z.number().int().positive(), updatedAt: z.iso.datetime(), updatedBy: z.string() });
const notesResponseSchema = z.object({ items: z.array(noteSchema), canEdit: z.boolean() });
export type PackagingNote = z.infer<typeof noteSchema>;

export function useWeek39PackagingNotes(snapshot: Week39PackagingSnapshot | null) {
  const [items, setItems] = useState<PackagingNote[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const reload = useCallback(async () => {
    if (!snapshot) return;
    try {
      const response = notesResponseSchema.parse(await apiRequest<unknown>(`/api/sarf39/manual-packaging/notes?weekStart=${snapshot?.weekStart ?? ""}`, { cache: "no-store" }));
      setItems(response.items); setCanEdit(response.canEdit); setError("");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Notlar yüklenemedi."); }
  }, [snapshot]);
  useEffect(() => { void reload(); }, [reload]);
  const save = async (input: { date: string; workOrderNo: string; productCode: string; text: string; expectedVersion: number }) => {
    setSaving(true); setError("");
    try {
      const saved = noteSchema.parse(await apiRequest<unknown>(`/api/sarf39/manual-packaging/notes?weekStart=${snapshot?.weekStart ?? ""}`, {
        method: "PUT", body: JSON.stringify(input),
      }));
      setItems((current) => [...current.filter((item) => !(item.date === saved.date && item.workOrderNo === saved.workOrderNo && item.productCode === saved.productCode)), saved]);
      return true;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Not kaydedilemedi.");
      return false;
    } finally { setSaving(false); }
  };
  const remove = async (input: { date: string; workOrderNo: string; productCode: string; expectedVersion: number }) => {
    setSaving(true); setError("");
    try {
      await apiRequest(`/api/sarf39/manual-packaging/notes?weekStart=${snapshot?.weekStart ?? ""}`, { method: "DELETE", body: JSON.stringify(input) });
      setItems((current) => current.filter((item) => !(item.date === input.date && item.workOrderNo === input.workOrderNo && item.productCode === input.productCode)));
      return true;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Not silinemedi.");
      return false;
    } finally { setSaving(false); }
  };
  return { items, canEdit, error, saving, reload, save, remove };
}
